import { PersonOutline, PhoneOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { BarChart } from "@mui/x-charts/BarChart";
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { esQuery } from '../../graphql/queries';

const PenetrationDialog = ({ open, campaignId, totalContacts, freshCount, suppressCount, inputStartDate, inputEndDate }) => {
	const userContext = useContext(UserContext);
	const client = generateClient();
	const [aggCounts, setAggCounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState('');
	const [totalDials, setTotalDials] = useState(0);

	useEffect(() => {
		if (open && campaignId) {
			getPenetrationData();
		}
	}, [open, campaignId]);

	function buildPageQuery(afterKey) {
		const agg = {
			"contact_composite": {
				"composite": {
					"size": 5000,
					"sources": [
						{ "contact": { "terms": { "field": "activityLogContactId.keyword" } } }
					]
				}
			}
		};

		if (afterKey) {
			agg.contact_composite.composite.after = afterKey;
		}

		return {
			"aggs": agg,
			"query": {
				"bool": {
					"must": [
						{ "term": { "type.keyword": "Dial" } },
						{ "term": { "tenant.keyword": userContext.tenantId } },
						{ "term": { "activityLogCampaignId.keyword": campaignId } },
						{
							"range": {
								"createdAt": {
									"gte": inputStartDate,
									"lte": inputEndDate
								}
							}
						}
					],
					"must_not": [
						{ "term": { "disposition.keyword": "Cadence Exhaustion - Retry Increment" } },
						{ "term": { "disposition.keyword": "Agent Specific — Agent Unavailable" } }
					]
				}
			},
			"size": 0
		};
	}

	async function getPenetrationData() {
		setLoading(true);
		setLoadingMessage('Loading penetration data...');

		try {
			let allBuckets = [];
			let afterKey = null;
			let pageCount = 0;

			while (true) {
				pageCount++;
				setLoadingMessage(`Loading penetration data... (page ${pageCount})`);

				const response = await client.graphql({
					query: esQuery,
					variables: {
						model: "activitylog-*",
						action: "_search",
						query: JSON.stringify(buildPageQuery(afterKey))
					}
				});

				const parsed = JSON.parse(response.data.esQuery);
				const buckets = parsed.aggregations.contact_composite.buckets;

				if (!buckets || buckets.length === 0) break;

				allBuckets = allBuckets.concat(buckets);
				afterKey = parsed.aggregations.contact_composite.after_key;

				if (buckets.length < 5000) break;
			}

			let callCount = 0;
			let attemptAggregations = {};

			for (const contact of allBuckets) {
				callCount += contact.doc_count;
				attemptAggregations[contact.doc_count] = attemptAggregations[contact.doc_count] ? attemptAggregations[contact.doc_count] + 1 : 1;
			}

			const chartData = [];
			for (const key in attemptAggregations) {
				chartData.push({ contacts: key, count: attemptAggregations[key] });
			}

			if (freshCount && freshCount > 0) {
				chartData.push({ contacts: 0, count: freshCount });
			}

			if (suppressCount && suppressCount > 0) {
				chartData.push({ contacts: 'Suppressed', count: suppressCount });
			}

			chartData.sort((a, b) => a.contacts - b.contacts);

			setAggCounts([...chartData]);
			setTotalDials(callCount);
		} catch (error) {
			console.error('Error fetching penetration data:', error);
		}

		setLoadingMessage('');
		setLoading(false);
	}

	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>

				{!loading &&
					<Box display="grid" gridTemplateColumns="2fr 1fr" gap={2} alignItems="center">
						<BarChart
							dataset={aggCounts}
							yAxis={[{ scaleType: 'band', dataKey: 'contacts' }]}
							series={[{ dataKey: 'count', color: '#b9cde8' }]}
							leftAxis={null}
							layout="horizontal"
							borderRadius={50}
							grid={{ vertical: true }}
							height={300}
							width={700}
							barLabel={(item) => {
								// Label the contact key
								const key = aggCounts[item.dataIndex].contacts;
								return `${key} Dial${key > 1 ? "s" : ""}`;
							}}
						/>
						<Box display="flex" flexDirection="column" gap={2}>
							<Box display="flex" gap={2} alignItems="center">
								<PhoneOutlined color="primary" fontSize="large" />
								<Box display="flex" flexDirection="column">
									<Typography variant="h4">{totalDials} Dials</Typography>
									{totalContacts && <Typography variant="h6">{totalContacts} Total Contacts</Typography>}
								</Box>
							</Box>
							{totalContacts && <Divider />}
							{totalContacts && <Box display="flex" gap={2} alignItems="center">
								<PersonOutline color="primary" fontSize="large" />
								<Box display="flex" flexDirection="column">
									<Typography variant="h6">Average Calls Per Contact</Typography>
									<Typography variant="h4">{(totalDials / totalContacts).toFixed(2)}</Typography>
								</Box>
							</Box>}
						</Box>


					</Box>
				}
				{loading &&
					<Box display="flex" flexDirection="column" alignItems="center" gap={1}>
						<CircularProgress color='primary' />
						{loadingMessage && <Typography variant="body2" color="textSecondary">{loadingMessage}</Typography>}
					</Box>
				}
			</Box>

		</>
	);
};

export default PenetrationDialog;
